import React, { useState, useEffect } from "react";
import { ShieldCheck, X, ExternalLink, CheckCircle2, AlertTriangle, Circle, FileText } from "lucide-react";
import api from "@/lib/api";

const STATUS_META = {
  checked: { icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", label: "Kontrollerad" },
  flagged: { icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50 border-amber-200", label: "Flaggad" },
  skipped: { icon: Circle, color: "text-zinc-400", bg: "bg-zinc-50 border-zinc-200", label: "Ej granskad" },
};

const KIND_LABEL = {
  chat: "AI-rådgivning",
  permit_check: "Bygglovs-bedömning",
  project_review: "Projektgranskning",
  drawing_review: "Ritningsgranskning",
};

export default function AIAuditBadge({ auditId, kind = "AI-utlåtande", label }) {
  const [open, setOpen] = useState(false);
  if (!auditId) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`ai-audit-${auditId}`}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-700 hover:bg-blue-50 px-2 py-1 rounded-sm border border-blue-200 transition">
        <ShieldCheck className="w-3 h-3" /> {label || "Visa AI-resonemang"}
      </button>
      {open && <AuditModal auditId={auditId} kind={kind} onClose={() => setOpen(false)} />}
    </>
  );
}

function AuditModal({ auditId, kind, onClose }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/ai/audit/${auditId}`)
      .then((r) => alive && setDoc(r.data))
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Kunde inte hämta audit"));
    return () => { alive = false; };
  }, [auditId]);

  const dateStr = doc?.created_at
    ? new Date(doc.created_at).toLocaleString("sv-SE")
    : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up max-h-[85vh] overflow-y-auto" data-testid="ai-audit-modal">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-700 font-semibold">AI-förklarbarhet</div>
            <h3 className="mt-1 text-xl font-medium" style={{ fontFamily: "Outfit" }}>
              {KIND_LABEL[doc?.kind] || KIND_LABEL[kind] || "AI-utlåtande"}
            </h3>
            {doc && (
              <p className="mt-1 text-xs text-zinc-500">
                {doc.model} · {dateStr}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </div>

        {err && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-sm">
            {err}
          </div>
        )}

        {!doc && !err && (
          <div className="text-sm text-zinc-500">Hämtar audit-data...</div>
        )}

        {doc && (
          <>
            <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-sm">
              <div className="text-xs text-blue-900 leading-relaxed">
                Detta är en spårbarhetslogg över vad AI:n har kontrollerat när
                utlåtandet skapades. Logg-id <code className="bg-white/60 px-1 py-0.5 rounded text-[10px]">{doc.audit_id}</code> kan
                refereras i ett beslut för fullständig spårbarhet enligt GDPR
                och förvaltningslagens krav på motivering.
              </div>
            </div>

            <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">
              Vilka regler kontrollerades?
            </h4>
            <ul className="space-y-2 mb-6">
              {doc.checklist?.map((item) => {
                const m = STATUS_META[item.status] || STATUS_META.skipped;
                const Icon = m.icon;
                return (
                  <li key={item.key}
                      className={`flex items-start gap-3 px-3 py-2.5 border rounded-sm ${m.bg}`}
                      data-testid={`audit-item-${item.key}`}>
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${m.color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-900">{item.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{item.ref}</div>
                      {item.comment && (
                        <div className="mt-1.5 text-xs italic text-zinc-600">&ldquo;{item.comment}&rdquo;</div>
                      )}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${m.color}`}>
                      {m.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            {doc.sources?.length > 0 && (
              <>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">
                  Källor som användes
                </h4>
                <ul className="space-y-2 mb-2">
                  {doc.sources.map((src, i) => (
                    <li key={i} className="flex items-start gap-2.5 px-3 py-2 border border-zinc-200 rounded-sm text-sm">
                      <FileText className="w-3.5 h-3.5 mt-0.5 text-zinc-500" />
                      <div className="flex-1">
                        <div className="font-medium text-zinc-900">{src.name}</div>
                        {src.license && <div className="text-xs text-zinc-500">Licens: {src.license}</div>}
                      </div>
                      {src.url && (
                        <a href={src.url} target="_blank" rel="noreferrer"
                          className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
                          Öppna <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
